import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Building2, RefreshCw, Plus, KeyRound, Trash2, ShieldCheck,
  Copy, Webhook, PlayCircle, UserPlus, Send, RotateCcw, Ban,
  CalendarClock, Link as LinkIcon, Mail, CheckCircle2, AlertTriangle,
  Clock, ShieldAlert, Activity, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import "./DeveloperPortal.css";
import { InviteToApplyForm } from "@/pages/admin/PartnerApplicationsAdmin";
import AdminPartnerRecovery from "@/pages/AdminPartnerRecovery";

// Match backend partners.service.ALL_SCOPES exactly. Kept as the fallback
// when the /scope-catalog endpoint hasn't loaded yet (offline / cold start).
const ALL_SCOPES = [
  "read:governance", "read:referrals", "read:volunteers", "read:resources",
  "read:appointments", "read:care-plans", "read:organizations",
  "write:referrals", "webhook:subscribe", "oauth:client",
  // Iter 77.2 additions.
  "read:clients", "write:clients",
  "read:careplans", "write:careplans",
  "read:documents", "write:documents",
  "read:tasks", "write:tasks",
  "read:consents", "write:consents",
  "read:reports", "read:audit",
];

// Iter 77.2 — used when the /scope-catalog endpoint call fails; keeps the
// UI functional with the flat list.
const FALLBACK_CATALOG = [{
  key: "all", label: "All scopes",
  helper: "Scope catalog metadata is temporarily unavailable; showing the flat list.",
  scopes: ALL_SCOPES.map((k) => ({
    key: k, label: k, category: "all", risk: "medium", kind: "read",
    description: "", legacy: false,
  })),
}];

const RISK_STYLES = {
  low: { bg: "#DCFCE7", fg: "#166534", label: "Low" },
  medium: { bg: "#FEF3C7", fg: "#92400E", label: "Medium" },
  high: { bg: "#FEE2E2", fg: "#991B1B", label: "High" },
};

function RiskPill({ risk }) {
  const s = RISK_STYLES[risk] || RISK_STYLES.medium;
  return (
    <span
      data-testid={`scope-risk-${risk}`}
      style={{
        display: "inline-block", padding: "0.1rem 0.4rem",
        borderRadius: "0.35rem", fontSize: "0.65rem",
        fontWeight: 600, background: s.bg, color: s.fg,
        marginLeft: "0.35rem",
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────── Iter 77.2B — Permission Profiles ─────────────────
// Enterprise IAM-style profile selector. Applies recommended scope
// bundles as a starting point; every scope remains individually
// editable and the summary panel updates live. When an admin modifies
// a profile-applied selection the label flips to "Custom (Modified)".
const PROFILE_CUSTOM_KEY = "__custom__";

const PRIVILEGE_STYLES = {
  none: { color: "#64748B", dot: "#94A3B8", label: "None" },
  read_only: { color: "#166534", dot: "#22C55E", label: "Read Only" },
  standard: { color: "#1E3A8A", dot: "#3B82F6", label: "Standard" },
  high: { color: "#991B1B", dot: "#EF4444", label: "High Privilege" },
  full: { color: "#7C2D12", dot: "#EA580C", label: "Full Integration" },
};

function PrivilegeBadge({ level }) {
  const s = PRIVILEGE_STYLES[level] || PRIVILEGE_STYLES.standard;
  return (
    <span
      data-testid={`profile-privilege-${level}`}
      style={{ display: "inline-flex", alignItems: "center",
                gap: "0.35rem", fontSize: "0.72rem", fontWeight: 600,
                color: s.color }}
    >
      <span aria-hidden="true"
             style={{ width: 8, height: 8, borderRadius: "50%",
                       background: s.dot, display: "inline-block" }} />
      {s.label}
    </span>
  );
}

function areSameSets(a, b) {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
}

function PermissionProfilePanel({
  profiles,               // enriched profiles from the API
  currentScopes,          // string[]
  onApply,                // (scopeArray) => void
  disabled,
  testIdPrefix = "ppp",
}) {
  const [selectedKey, setSelectedKey] = useState(PROFILE_CUSTOM_KEY);
  const [appliedProfile, setAppliedProfile] = useState(null);
  const [confirmHighRisk, setConfirmHighRisk] = useState(null);
  const [suppressHighRiskDialog, setSuppressHighRiskDialog] = useState(false);

  // Detect whether the current scope set still matches the applied
  // profile — if not we automatically flip to "Custom (Modified)".
  const isModified = appliedProfile
    && !areSameSets(appliedProfile.recommended_scopes, currentScopes);

  const selectedProfile = (profiles || []).find((p) => p.key === selectedKey);

  const actuallyApply = (profile) => {
    onApply([...profile.recommended_scopes]);
    setAppliedProfile(profile);
  };

  const handleSelect = (key) => {
    setSelectedKey(key);
    if (key === PROFILE_CUSTOM_KEY) return;
    const p = (profiles || []).find((x) => x.key === key);
    if (!p) return;
    if (p.contains_high_risk && !suppressHighRiskDialog) {
      setConfirmHighRisk(p);
      return;
    }
    actuallyApply(p);
  };

  // Live summary of the CURRENT selection (not the applied profile).
  const liveSummary = useMemo(() => {
    const s = { read: 0, write: 0, meta: 0, low: 0, medium: 0, high: 0 };
    (currentScopes || []).forEach((k) => {
      const meta = (profiles || [])
        .flatMap((p) => p.scope_details || [])
        .find((sd) => sd.key === k);
      if (meta) {
        s[meta.kind] = (s[meta.kind] || 0) + 1;
        s[meta.risk] = (s[meta.risk] || 0) + 1;
      }
    });
    let overall = "read_only";
    if (s.high > 0) overall = "high";
    else if (s.write > 0) overall = "standard";
    else if ((s.read + s.meta) === 0) overall = "none";
    return { ...s, overall };
  }, [currentScopes, profiles]);

  return (
    <div
      data-testid={`${testIdPrefix}-panel`}
      style={{
        border: "1px solid #E5E5E0", borderRadius: "0.75rem",
        padding: "1rem", marginBottom: "1rem",
        background: "linear-gradient(180deg, #FAFAF7 0%, #FFFFFF 100%)",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr",
                      gap: "1.25rem", alignItems: "start" }}>
        {/* ── LEFT: selector + description ────────────────────────── */}
        <div>
          <label
            htmlFor={`${testIdPrefix}-select`}
            style={{ display: "block", fontSize: "0.72rem",
                       fontWeight: 700, color: "#0F172A",
                       textTransform: "uppercase", letterSpacing: "0.05em",
                       marginBottom: "0.35rem" }}
          >
            Permission Profile
          </label>
          <select
            id={`${testIdPrefix}-select`}
            data-testid={`${testIdPrefix}-select`}
            aria-label="Permission profile"
            disabled={disabled || !profiles?.length}
            value={selectedKey}
            onChange={(e) => handleSelect(e.target.value)}
            style={{
              width: "100%", padding: "0.55rem 0.7rem",
              borderRadius: "0.5rem", border: "1px solid #CBD5E1",
              background: "#FFFFFF", fontSize: "0.9rem",
              color: "#0F172A",
            }}
          >
            <option value={PROFILE_CUSTOM_KEY}>Custom (Default)</option>
            {(profiles || []).map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>

          <div
            data-testid={`${testIdPrefix}-status-line`}
            style={{ marginTop: "0.5rem", fontSize: "0.72rem", color: "#64748B" }}
          >
            {selectedKey === PROFILE_CUSTOM_KEY && !appliedProfile && (
              <>Configuring individual scopes manually.</>
            )}
            {selectedKey === PROFILE_CUSTOM_KEY && appliedProfile && (
              <span data-testid={`${testIdPrefix}-modified-note`}>
                <strong style={{ color: "#92400E" }}>Custom</strong>
                {" · Modified from: "}
                <strong>{appliedProfile.label}</strong>
              </span>
            )}
            {selectedKey !== PROFILE_CUSTOM_KEY && !isModified && (
              <>Profile applied — you may still edit any scope below.</>
            )}
            {selectedKey !== PROFILE_CUSTOM_KEY && isModified && (
              <span data-testid={`${testIdPrefix}-modified-note`}>
                <strong style={{ color: "#92400E" }}>Custom (Modified)</strong>
                {" · From: "}
                <strong>{selectedProfile?.label}</strong>
              </span>
            )}
          </div>

          {selectedProfile && (
            <div
              data-testid={`${testIdPrefix}-description`}
              style={{
                marginTop: "0.85rem",
                padding: "0.75rem 0.9rem",
                background: "#F1F5F9",
                borderRadius: "0.6rem",
                borderLeft: "3px solid #1E3A8A",
              }}
            >
              <div style={{ display: "flex", alignItems: "center",
                              justifyContent: "space-between", gap: "0.5rem",
                              marginBottom: "0.35rem" }}>
                <strong style={{ fontSize: "0.85rem", color: "#0F172A" }}>
                  {selectedProfile.label}
                </strong>
                <PrivilegeBadge level={selectedProfile.privilege_level} />
              </div>
              <div style={{ fontSize: "0.78rem", color: "#334155", lineHeight: 1.45 }}>
                {selectedProfile.description}
              </div>
              <div style={{ marginTop: "0.65rem", fontSize: "0.72rem",
                              fontWeight: 700, color: "#0F172A",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em" }}>
                Included permissions
              </div>
              <ul
                data-testid={`${testIdPrefix}-included`}
                style={{ margin: "0.35rem 0 0 0", padding: 0,
                          listStyle: "none", display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                          gap: "0.15rem 0.5rem", fontSize: "0.78rem",
                          color: "#0F172A" }}
              >
                {(selectedProfile.scope_details || []).map((s) => (
                  <li key={s.key}
                       data-testid={`${testIdPrefix}-included-${s.key}`}
                       style={{ display: "flex", alignItems: "center",
                                  gap: "0.35rem" }}>
                    <CheckCircle2 aria-hidden="true"
                                   style={{ width: 14, height: 14,
                                              color: s.risk === "high"
                                                        ? "#EF4444" : "#22C55E" }} />
                    <span style={{ fontFamily: "monospace" }}>{s.key}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── RIGHT: live permission summary ─────────────────────── */}
        <div
          data-testid={`${testIdPrefix}-summary`}
          role="region"
          aria-label="Permission summary"
          style={{
            border: "1px solid #E5E5E0", borderRadius: "0.6rem",
            padding: "0.85rem 0.9rem", background: "#FFFFFF",
          }}
        >
          <div style={{ fontSize: "0.72rem", fontWeight: 700,
                          color: "#0F172A", textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: "0.5rem" }}>
            Permission Summary
          </div>
          <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: "0.4rem 0.9rem", fontSize: "0.78rem" }}>
            <SummaryRow label="Read Scopes" value={liveSummary.read}
                          testId={`${testIdPrefix}-summary-read`} />
            <SummaryRow label="Write Scopes" value={liveSummary.write}
                          testId={`${testIdPrefix}-summary-write`} />
            <SummaryRow label="High Risk" value={liveSummary.high} tone="high"
                          testId={`${testIdPrefix}-summary-risk-high`} />
            <SummaryRow label="Medium Risk" value={liveSummary.medium} tone="medium"
                          testId={`${testIdPrefix}-summary-risk-medium`} />
            <SummaryRow label="Low Risk" value={liveSummary.low} tone="low"
                          testId={`${testIdPrefix}-summary-risk-low`} />
            <SummaryRow label="Meta Scopes" value={liveSummary.meta}
                          testId={`${testIdPrefix}-summary-meta`} />
          </div>
          <div style={{ marginTop: "0.7rem", paddingTop: "0.55rem",
                          borderTop: "1px solid #F1F0EA",
                          display: "flex", justifyContent: "space-between",
                          alignItems: "center" }}>
            <span style={{ fontSize: "0.72rem", fontWeight: 700,
                              color: "#0F172A", textTransform: "uppercase",
                              letterSpacing: "0.04em" }}>Overall Access</span>
            <PrivilegeBadge level={liveSummary.overall} />
          </div>
        </div>
      </div>

      {confirmHighRisk && (
        <HighRiskConfirm
          profile={confirmHighRisk}
          suppress={suppressHighRiskDialog}
          onToggleSuppress={setSuppressHighRiskDialog}
          onCancel={() => {
            setConfirmHighRisk(null);
            setSelectedKey(appliedProfile ? appliedProfile.key : PROFILE_CUSTOM_KEY);
          }}
          onConfirm={() => {
            actuallyApply(confirmHighRisk);
            setConfirmHighRisk(null);
          }}
        />
      )}
    </div>
  );
}

function SummaryRow({ label, value, tone, testId }) {
  const toneStyles = {
    high: { color: "#991B1B", bg: "#FEE2E2" },
    medium: { color: "#92400E", bg: "#FEF3C7" },
    low: { color: "#166534", bg: "#DCFCE7" },
  };
  const t = toneStyles[tone];
  return (
    <div style={{ display: "flex", alignItems: "center",
                    justifyContent: "space-between" }}
          data-testid={testId}>
      <span style={{ color: "#64748B" }}>{label}</span>
      <span
        style={{
          fontWeight: 600,
          padding: t ? "0.05rem 0.5rem" : "0",
          borderRadius: "0.35rem",
          background: t?.bg || "transparent",
          color: t?.color || "#0F172A",
          minWidth: t ? "1.8rem" : "auto",
          textAlign: "center",
        }}
      >{value}</span>
    </div>
  );
}

function HighRiskConfirm({ profile, suppress, onToggleSuppress, onCancel, onConfirm }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="high-risk-title"
      data-testid="ppp-high-risk-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 60, padding: "1rem",
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF", borderRadius: "1rem",
          padding: "1.5rem", maxWidth: "480px", width: "100%",
          boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
        }}
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
          <ShieldAlert style={{ width: 24, height: 24, color: "#EF4444",
                                   flexShrink: 0 }} aria-hidden="true" />
          <div style={{ flex: 1 }}>
            <h3 id="high-risk-title"
                 style={{ margin: 0, fontSize: "1.05rem", color: "#0F172A" }}>
              High Privilege Permission Profile
            </h3>
            <p style={{ margin: "0.5rem 0 0", fontSize: "0.85rem",
                          color: "#334155", lineHeight: 1.5 }}>
              The selected profile <strong>{profile.label}</strong> grants
              permissions that allow external systems to modify HarborWell
              protected information. Do you wish to continue?
            </p>
            <div style={{ marginTop: "0.6rem", fontSize: "0.72rem",
                            color: "#991B1B" }}>
              High-risk permissions in this profile:{" "}
              <strong>
                {(profile.scope_details || [])
                  .filter((s) => s.risk === "high")
                  .map((s) => s.key).join(", ") || "—"}
              </strong>
            </div>
            <label style={{ display: "flex", alignItems: "center",
                              gap: "0.4rem", marginTop: "0.9rem",
                              fontSize: "0.75rem", color: "#334155" }}>
              <input
                type="checkbox"
                checked={suppress}
                onChange={(e) => onToggleSuppress(e.target.checked)}
                data-testid="ppp-high-risk-suppress"
              />
              Do not show this dialog again for this editing session
            </label>
          </div>
        </div>
        <div style={{ marginTop: "1.1rem", display: "flex", gap: "0.5rem",
                        justifyContent: "flex-end" }}>
          <button
            className="hw-partners__btn"
            onClick={onCancel}
            data-testid="ppp-high-risk-cancel"
          >Cancel</button>
          <button
            className="hw-partners__btn hw-partners__btn--primary"
            onClick={onConfirm}
            data-testid="ppp-high-risk-confirm"
          >Apply Profile</button>
        </div>
      </div>
    </div>
  );
}

// Iter 77.2 — Grouped scope checklist. Renders each category as a
// collapsible section with helper text; each scope row shows label +
// legacy tag + risk pill and an inline description tooltip via the
// native `title` attribute.
function GroupedScopeChecklist({ catalog, selected, onToggle, testIdPrefix, filterToKeys }) {  const groups = (catalog || FALLBACK_CATALOG).map((g) => ({
    ...g,
    scopes: filterToKeys
      ? g.scopes.filter((s) => filterToKeys.includes(s.key))
      : g.scopes,
  })).filter((g) => g.scopes.length > 0);
  return (
    <div data-testid={`${testIdPrefix}-groups`} style={{ display: "grid", gap: "0.75rem" }}>
      {groups.map((g) => (
        <fieldset
          key={g.key}
          data-testid={`${testIdPrefix}-group-${g.key}`}
          style={{
            border: "1px solid #E5E5E0", borderRadius: "0.6rem",
            padding: "0.6rem 0.75rem", margin: 0,
          }}
        >
          <legend style={{ padding: "0 0.35rem", fontSize: "0.75rem", fontWeight: 600, color: "#0F172A" }}>
            {g.label}
          </legend>
          {g.helper && (
            <div style={{ fontSize: "0.68rem", color: "#64748B", marginBottom: "0.35rem" }}>
              {g.helper}
            </div>
          )}
          <div className="hw-partners__scope-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
            {g.scopes.map((s) => (
              <label
                key={s.key}
                data-testid={`${testIdPrefix}-scope-${s.key}`}
                title={s.description || s.key}
                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(s.key)}
                  onChange={() => onToggle(s.key)}
                />
                <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", fontSize: "0.78rem" }}>
                  <span style={{ fontFamily: "monospace" }}>{s.key}</span>
                  {s.legacy && (
                    <span
                      data-testid={`scope-legacy-${s.key}`}
                      style={{
                        marginLeft: "0.3rem", fontSize: "0.6rem",
                        padding: "0.05rem 0.3rem", borderRadius: "0.3rem",
                        background: "#EEF2FF", color: "#3730A3", fontWeight: 600,
                      }}
                    >LEGACY</span>
                  )}
                  <RiskPill risk={s.risk} />
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

const ORG_TYPES = [
  {
    key: "api_only",
    label: "API Integration Only",
    hint: "System-to-system integration. No Portal Owner or portal login required.",
  },
  {
    key: "portal",
    label: "Partner Portal Access",
    hint: "Human collaborators use the Partner Portal. Requires a Portal Owner invitation.",
  },
  {
    key: "api_and_portal",
    label: "API + Partner Portal",
    hint: "Both API integration and Portal Owner access. Invite the Portal Owner right after creation.",
  },
];

const orgTypeLabel = (key) =>
  ORG_TYPES.find((t) => t.key === key)?.label || "API + Partner Portal";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const INV_TONE = {
  pending: "warning", sent: "info", delivered: "info",
  activated: "success", revoked: "danger", expired: "danger",
};

function StatusPill({ status }) {
  const cls = status === "active" ? "active" : status === "suspended" ? "suspended" : "archived";
  return <span className={`hw-partners__pill hw-partners__pill--${cls}`}>{status}</span>;
}

function CopyBtn({ value }) {
  return (
    <button className="hw-partners__btn" style={{ padding: "0.15rem 0.45rem" }}
            onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}>
      <Copy className="w-3 h-3" />
    </button>
  );
}

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState(null);
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showNewKey, setShowNewKey] = useState(false);
  const [showNewSub, setShowNewSub] = useState(false);
  const [freshToken, setFreshToken] = useState(null);
  // Iter 77.2 — grouped scope catalog loaded from the backend so admins
  // see risk + description metadata when granting new scopes.
  const [scopeCatalog, setScopeCatalog] = useState(null);
  // Iter 77.2B — Enterprise Permission Profiles.
  const [permissionProfiles, setPermissionProfiles] = useState(null);
  // Portal Owner onboarding state
  const [portalOwnerStatus, setPortalOwnerStatus] = useState(null);
  const [showInviteOwner, setShowInviteOwner] = useState(false);
  // freshActivationUrl: keyed by invitation_id → one-time URL from the server
  const [freshActivationUrls, setFreshActivationUrls] = useState({});

  // Iter 76.6/76.7 — "Invite a partner to apply" lives here next to
  // "New partner", but the Outreach LIST remains on
  // /admin/partner-applications so this page stays focused on orgs.
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partners");
      setPartners(data.partners || []);
      const { data: ev } = await api.get("/admin/webhooks/events");
      setEvents(ev.events || []);
      // Iter 77.2 — best-effort catalog fetch; falls back silently.
      try {
        const { data: cat } = await api.get("/admin/partners/scope-catalog");
        setScopeCatalog(cat.categories || null);
      } catch { setScopeCatalog(null); }
      try {
        const { data: pf } = await api.get("/admin/partners/permission-profiles");
        setPermissionProfiles(pf.profiles || null);
      } catch { setPermissionProfiles(null); }
    } catch (e) { toast.error(formatApiError(e, "Failed to load partners")); }
    finally { setLoading(false); }
  }, []);

  const loadPartnerDetail = useCallback(async (partnerId) => {
    try {
      const [kd, ud, sd, det] = await Promise.all([
        api.get(`/admin/partners/${partnerId}/keys`),
        api.get(`/admin/partners/${partnerId}/usage`),
        api.get(`/admin/webhooks/subscriptions?partner_org_id=${partnerId}`),
        api.get(`/admin/partners/${partnerId}`),
      ]);
      setKeys(kd.data.keys || []);
      setUsage(ud.data);
      setSubs(sd.data.subscriptions || []);
      setPortalOwnerStatus(det.data.portal_owner_status || null);
      // Keep the selected partner in sync (may have org_type update from server)
      setSelected((prev) => (prev && prev.partner_id === partnerId
        ? { ...prev, ...det.data, portal_owner_status: undefined }
        : prev));
    } catch (e) { toast.error(formatApiError(e, "Failed to load partner")); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (selected) loadPartnerDetail(selected.partner_id); }, [selected, loadPartnerDetail]);

  const createPartner = async (form) => {
    try {
      const { data } = await api.post("/admin/partners", form);
      toast.success(`Partner "${data.name}" created`);
      setShowNew(false);
      await load();
      setSelected(data);
      // Auto-open the invite dialog for portal-enabled org types.
      if (data.org_type === "api_and_portal" || data.org_type === "portal") {
        // Slight delay so the drawer mounts first
        setTimeout(() => setShowInviteOwner(true), 200);
      }
    } catch (e) { toast.error(formatApiError(e, "Create failed")); }
  };

  const invitePortalOwner = async (form) => {
    try {
      const { data } = await api.post(
        `/admin/partners/${selected.partner_id}/invite-portal-owner`, form,
      );
      const emailOk = data?.status === "sent";
      toast.success(emailOk
        ? "Invitation sent to Portal Owner"
        : "Invitation created (email delivery failed — copy the link below)");
      if (data?.activation_url) {
        setFreshActivationUrls((prev) => ({
          ...prev, [data.id]: data.activation_url,
        }));
      }
      setShowInviteOwner(false);
      await loadPartnerDetail(selected.partner_id);
    } catch (e) { toast.error(formatApiError(e, "Invite failed")); }
  };

  const invitationAction = async (invId, action, body = {}, label = "Action") => {
    try {
      const { data } = await api.post(
        `/admin/partners/${selected.partner_id}/invitations/${invId}/${action}`,
        body,
      );
      toast.success(`${label} complete`);
      if (data?.activation_url) {
        setFreshActivationUrls((prev) => ({
          ...prev, [invId]: data.activation_url,
        }));
      }
      await loadPartnerDetail(selected.partner_id);
    } catch (e) { toast.error(formatApiError(e, `${label} failed`)); }
  };

  /* ----- "Invite a partner to apply" — form lives here, list on /admin/partner-applications ----- */
  const inviteToApply = async (payload) => {
    try {
      const { data } = await api.post("/admin/partner-outreach/invites", payload);
      if (data.email_delivery_ok) {
        toast.success(
          <span>
            Invitation sent to <strong>{data.email}</strong>. View in{" "}
            <Link
              to="/admin/partner-applications"
              className="underline text-[#1E3A8A]"
              data-testid="outreach-toast-view-link"
            >
              Outreach
            </Link>.
          </span>
        );
      } else {
        toast.warning(
          <span>
            Invitation saved but email delivery failed. Open{" "}
            <Link to="/admin/partner-applications" className="underline text-[#1E3A8A]">
              Outreach
            </Link>{" "}
            to copy the apply link.
          </span>
        );
      }
      setShowInvite(false);
    } catch (e) { toast.error(formatApiError(e, "Invite failed")); }
  };

  const mintKey = async (form) => {
    try {
      const { data } = await api.post(`/admin/partners/${selected.partner_id}/keys`, form);
      setFreshToken(data);
      setShowNewKey(false);
      await loadPartnerDetail(selected.partner_id);
      toast.success("API key minted — copy the secret now");
    } catch (e) { toast.error(formatApiError(e, "Mint failed")); }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/partners/${selected.partner_id}/keys/${keyId}`);
      toast.success("Key revoked");
      await loadPartnerDetail(selected.partner_id);
    } catch (e) { toast.error(formatApiError(e, "Revoke failed")); }
  };

  const createSub = async (form) => {
    try {
      const { data } = await api.post("/admin/webhooks/subscriptions",
        { ...form, partner_org_id: selected.partner_id });
      setShowNewSub(false);
      await loadPartnerDetail(selected.partner_id);
      toast.success("Webhook subscription created");
      // Show the secret on the freshly created row.
      setFreshToken({ subscription: data });
    } catch (e) { toast.error(formatApiError(e, "Create subscription failed")); }
  };

  const rotateSubSecret = async (subId) => {
    if (!window.confirm("Rotate this subscription's signing secret? The previous secret will stop working immediately.")) return;
    try {
      const { data } = await api.post(`/admin/webhooks/subscriptions/${subId}/rotate-secret`);
      setFreshToken({ subscription: data });
      await loadPartnerDetail(selected.partner_id);
      toast.success("Secret rotated");
    } catch (e) { toast.error(formatApiError(e, "Rotate failed")); }
  };

  const deleteSub = async (subId) => {
    if (!window.confirm("Delete this subscription?")) return;
    try {
      await api.delete(`/admin/webhooks/subscriptions/${subId}`);
      await loadPartnerDetail(selected.partner_id);
      toast.success("Subscription deleted");
    } catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  return (
    <div className="hw-partners" data-testid="admin-partners">
      <div className="hw-partners__header">
        <div>
          <h2><Building2 className="w-5 h-5" style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
            Partner Organizations</h2>
          <p className="hw-partners__sub">
            First-class tenancy for nonprofit and community partners.
            Each partner has scoped API keys, OAuth client credentials,
            and webhook subscriptions with HMAC-signed delivery.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          <button className="hw-partners__btn" onClick={load} data-testid="partners-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
          </button>
          <button className="hw-partners__btn hw-partners__btn--primary"
                  onClick={() => setShowNew(true)} data-testid="partners-new">
            <Plus className="w-3.5 h-3.5" /> New partner
          </button>
          <button
            className="hw-partners__btn hw-partners__btn--primary"
            onClick={() => setShowInvite(true)}
            data-testid="partners-invite-to-apply"
          >
            <UserPlus className="w-3.5 h-3.5" /> Invite a partner to apply
          </button>
        </div>
      </div>

      {showNew && (
        <NewPartnerForm
          onCancel={() => setShowNew(false)}
          onSubmit={createPartner}
          catalog={scopeCatalog}
          profiles={permissionProfiles}
        />
      )}
      {showInvite && (
        <InviteToApplyForm
          onCancel={() => setShowInvite(false)}
          onSubmit={inviteToApply}
        />
      )}

      <section className="hw-partners__section" data-testid="partners-table-section">
        <table className="hw-partners__table">
          <thead>
            <tr>
              <th>Name</th><th>Type</th><th>Contact</th>
              <th>Scopes</th><th>Status</th><th>Created</th><th></th>
            </tr>
          </thead>
          <tbody>
            {partners.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: "2rem", textAlign: "center", color: "#64748B" }}>
                {loading ? "Loading…" : "No partners yet. Create one to enable external integrations."}
              </td></tr>
            ) : partners.map((p) => (
              <tr key={p.partner_id} data-testid={`partner-row-${p.slug}`}>
                <td>
                  <strong>{p.name}</strong>
                  <div style={{ fontSize: "0.7rem", color: "#64748B" }}>
                    <code>{p.slug}</code>
                  </div>
                </td>
                <td>
                  <span
                    data-testid={`partner-type-${p.slug}`}
                    style={{
                      fontSize: "0.7rem",
                      padding: "0.15rem 0.5rem",
                      borderRadius: "999px",
                      background: p.org_type === "api_only" ? "#EFF6FF" : "#ECFDF5",
                      color: p.org_type === "api_only" ? "#1E40AF" : "#065F46",
                    }}
                  >
                    {orgTypeLabel(p.org_type || "api_and_portal")}
                  </span>
                </td>
                <td>{p.contact_email}</td>
                <td>{(p.allowed_scopes || []).length}</td>
                <td><StatusPill status={p.status} /></td>
                <td style={{ fontSize: "0.75rem", color: "#64748B" }}>{p.created_at?.slice(0, 10)}</td>
                <td>
                  <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                    <button className="hw-partners__btn" onClick={() => setSelected(p)}
                            data-testid={`partner-manage-${p.slug}`}>Manage</button>
                    <Link
                      to={`/admin/partners/${p.partner_id}/onboarding-wizard`}
                      className="hw-partners__btn"
                      style={{ background: "#EEF2FF", color: "#3730A3",
                                border: "1px solid #C7D2FE", textDecoration: "none" }}
                      data-testid={`partner-onboarding-wizard-${p.slug}`}
                      title="Advisory framework recommendation — no permissions granted">
                      Onboarding wizard
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {selected && (
        <PartnerDetail
          partner={selected} keys={keys} usage={usage} subs={subs} events={events}
          onClose={() => { setSelected(null); setFreshToken(null); setShowNewKey(false);
                             setShowNewSub(false); setShowInviteOwner(false);
                             setPortalOwnerStatus(null); setFreshActivationUrls({}); }}
          onMintKey={() => setShowNewKey(true)}
          onRevokeKey={revokeKey}
          onNewSub={() => setShowNewSub(true)}
          onRotateSecret={rotateSubSecret}
          onDeleteSub={deleteSub}
          freshToken={freshToken}
          onClearFresh={() => setFreshToken(null)}
          showNewKey={showNewKey}
          showNewSub={showNewSub}
          onCancelNewKey={() => setShowNewKey(false)}
          onCancelNewSub={() => setShowNewSub(false)}
          onSubmitNewKey={mintKey}
          onSubmitNewSub={createSub}
          portalOwnerStatus={portalOwnerStatus}
          showInviteOwner={showInviteOwner}
          onOpenInviteOwner={() => setShowInviteOwner(true)}
          onCancelInviteOwner={() => setShowInviteOwner(false)}
          onSubmitInviteOwner={invitePortalOwner}
          onInvitationAction={invitationAction}
          freshActivationUrls={freshActivationUrls}
          catalog={scopeCatalog}
        />
      )}
    </div>
  );
}

function NewPartnerForm({ onCancel, onSubmit, catalog, profiles }) {
  const [form, setForm] = useState({
    name: "", contact_email: "", contact_name: "",
    website: "", notes: "",
    // Iter 77.2 — default grant remains minimal (governance BI only);
    // new scopes are NEVER auto-selected.
    allowed_scopes: ["read:governance"],
    org_type: "api_and_portal",
  });
  const toggleScope = (s) => setForm((f) => ({
    ...f, allowed_scopes: f.allowed_scopes.includes(s)
      ? f.allowed_scopes.filter((x) => x !== s) : [...f.allowed_scopes, s],
  }));
  const applyProfile = (scopes) => setForm((f) => ({ ...f, allowed_scopes: scopes }));
  return (
    <section className="hw-partners__section" data-testid="partners-new-form">
      <h3 style={{ margin: 0 }}>New Partner Organization</h3>

      <h4 style={{ margin: "0.9rem 0 0.35rem", fontSize: "0.8rem" }}>Organization type</h4>
      <div className="hw-partners__scope-grid" style={{ gridTemplateColumns: "1fr" }}>
        {ORG_TYPES.map((t) => (
          <label
            key={t.key}
            data-testid={`pnf-org-type-${t.key}`}
            style={{
              display: "flex", alignItems: "flex-start", gap: "0.6rem",
              padding: "0.5rem 0.7rem", borderRadius: "0.5rem",
              border: form.org_type === t.key ? "1.5px solid #1E3A8A" : "1px solid #E5E5E0",
              background: form.org_type === t.key ? "#F1F5F9" : "#FFF",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name="org_type"
              value={t.key}
              checked={form.org_type === t.key}
              onChange={() => setForm({ ...form, org_type: t.key })}
              style={{ marginTop: "0.2rem" }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: "0.8rem", color: "#0F172A" }}>{t.label}</div>
              <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: "0.15rem" }}>{t.hint}</div>
            </div>
          </label>
        ))}
      </div>

      <div className="hw-partners__form-grid" style={{ marginTop: "0.75rem" }}>
        <label className="hw-partners__label">Name
          <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                 data-testid="pnf-name" /></label>
        <label className="hw-partners__label">Contact email
          <input type="email" value={form.contact_email}
                 onChange={(e) => setForm({...form, contact_email: e.target.value})}
                 data-testid="pnf-email" /></label>
        <label className="hw-partners__label">Contact name
          <input value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} /></label>
        <label className="hw-partners__label">Website
          <input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} /></label>
      </div>
      <h4 style={{ margin: "0.9rem 0 0.35rem", fontSize: "0.8rem" }}>Allowed scopes</h4>
      <p style={{ fontSize: "0.72rem", color: "#64748B", margin: "0 0 0.5rem" }}>
        Least-privilege by default. Grant only the scopes this partner truly needs.
        Reading a resource NEVER implies the ability to write it, and vice-versa.
      </p>
      <PermissionProfilePanel
        profiles={profiles}
        currentScopes={form.allowed_scopes}
        onApply={applyProfile}
        testIdPrefix="pnf-ppp"
      />
      <GroupedScopeChecklist
        catalog={catalog}
        selected={form.allowed_scopes}
        onToggle={toggleScope}
        testIdPrefix="pnf-allowed-scopes"
      />
      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.4rem" }}>
        <button className="hw-partners__btn" onClick={onCancel}>Cancel</button>
        <button className="hw-partners__btn hw-partners__btn--primary"
                onClick={() => onSubmit(form)}
                disabled={!form.name || !form.contact_email}
                data-testid="pnf-submit">Create partner</button>
      </div>
    </section>
  );
}

function NewKeyForm({ partner, onCancel, onSubmit, catalog }) {
  const [name, setName] = useState("primary-key");
  const [scopes, setScopes] = useState(partner.allowed_scopes || []);
  const [rateLimit, setRateLimit] = useState(300);
  const toggle = (s) => setScopes((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  return (
    <section className="hw-partners__section" data-testid="key-mint-form">
      <h3 style={{ margin: 0 }}>Mint API key for {partner.name}</h3>
      <div className="hw-partners__form-grid">
        <label className="hw-partners__label">Key name
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="kmf-name" /></label>
        <label className="hw-partners__label">Rate limit per minute
          <input type="number" value={rateLimit} onChange={(e) => setRateLimit(Number(e.target.value))} /></label>
      </div>
      <h4 style={{ margin: "0.9rem 0 0.35rem", fontSize: "0.8rem" }}>Scopes (must be a subset of partner&apos;s allowed scopes)</h4>
      <GroupedScopeChecklist
        catalog={catalog}
        selected={scopes}
        onToggle={toggle}
        testIdPrefix="kmf-scopes"
        filterToKeys={partner.allowed_scopes || []}
      />
      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.4rem" }}>
        <button className="hw-partners__btn" onClick={onCancel}>Cancel</button>
        <button className="hw-partners__btn hw-partners__btn--primary"
                onClick={() => onSubmit({ name, scopes, rate_limit_per_min: rateLimit })}
                data-testid="kmf-submit">Mint key</button>
      </div>
    </section>
  );
}

function NewSubForm({ partner, events, onCancel, onSubmit }) {
  const [url, setUrl] = useState("https://");
  const [chosen, setChosen] = useState([]);
  const [description, setDescription] = useState("");
  const toggle = (e) => setChosen((cur) => cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]);
  return (
    <section className="hw-partners__section" data-testid="sub-new-form">
      <h3 style={{ margin: 0 }}>New Webhook Subscription for {partner.name}</h3>
      <div className="hw-partners__form-grid">
        <label className="hw-partners__label">Target URL
          <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="snf-url" /></label>
        <label className="hw-partners__label">Description
          <input value={description} onChange={(e) => setDescription(e.target.value)} /></label>
      </div>
      <h4 style={{ margin: "0.9rem 0 0.35rem", fontSize: "0.8rem" }}>Subscribe to events ({chosen.length} selected)</h4>
      <div className="hw-partners__scope-grid" style={{ maxHeight: "260px", overflowY: "auto" }}>
        {events.map((e) => (
          <label key={e.event_type}>
            <input type="checkbox" checked={chosen.includes(e.event_type)}
                   onChange={() => toggle(e.event_type)} />
            <span>{e.event_type}</span>
          </label>
        ))}
      </div>
      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.4rem" }}>
        <button className="hw-partners__btn" onClick={onCancel}>Cancel</button>
        <button className="hw-partners__btn hw-partners__btn--primary"
                onClick={() => onSubmit({ target_url: url, event_types: chosen, description })}
                disabled={!url.startsWith("http") || chosen.length === 0}
                data-testid="snf-submit">Create subscription</button>
      </div>
    </section>
  );
}

function PartnerDetail({ partner, keys, usage, subs, events, freshToken, onClearFresh,
                          onClose, onMintKey, onRevokeKey, onNewSub, onRotateSecret, onDeleteSub,
                          showNewKey, showNewSub, onCancelNewKey, onCancelNewSub,
                          onSubmitNewKey, onSubmitNewSub,
                          portalOwnerStatus, showInviteOwner, onOpenInviteOwner,
                          onCancelInviteOwner, onSubmitInviteOwner,
                          onInvitationAction, freshActivationUrls, catalog }) {
  const orgType = partner.org_type || "api_and_portal";
  const supportsPortal = orgType !== "api_only";
  const st = portalOwnerStatus || {};
  const latest = st.latest_invitation || null;
  const owner = st.portal_owner || null;
  // Show the "no portal owner" banner when the partner is portal-enabled AND
  // there's no active portal owner AND no active invitation. An invitation
  // that's revoked/expired shouldn't suppress the banner — the partner
  // effectively has no way in until someone re-invites.
  const invActive = latest && ["pending", "sent", "delivered"].includes(latest.status);
  const ownerActive = owner && owner.status === "active";
  const showNoOwnerBanner = supportsPortal && !invActive && !ownerActive;
  const [activeTab, setActiveTab] = useState("overview");
  return (
    <div className="hw-exec__drawer-backdrop" onClick={onClose}>
      <div className="hw-exec__drawer" onClick={(e) => e.stopPropagation()}
           data-testid="partner-drawer" style={{ width: "min(920px, 100vw)" }}>
        <div className="hw-exec__drawer-head">
          <div>
            <div className="hw-exec__drawer-crumb">
              Partner Organization · {orgTypeLabel(orgType)}
            </div>
            <div className="hw-exec__drawer-title">
              <Building2 className="w-5 h-5" /> {partner.name}
              <StatusPill status={partner.status} />
            </div>
            <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: "0.15rem" }}>
              {partner.contact_email} · slug <code>{partner.slug}</code>
            </div>
          </div>
          <button className="hw-partners__btn" onClick={onClose} data-testid="partner-drawer-close">Close</button>
        </div>

        {/* Tab strip */}
        <div
          role="tablist"
          aria-label="Partner drawer tabs"
          style={{
            display: "flex",
            gap: "0.25rem",
            padding: "0 1.15rem",
            borderBottom: "1px solid #E2E8F0",
            background: "#F8FAFC",
          }}
        >
          {[
            { key: "overview", label: "Overview", icon: Building2, testId: "partner-tab-overview" },
            { key: "lifecycle", label: "Lifecycle", icon: Activity, testId: "partner-tab-lifecycle" },
            { key: "security", label: "Recovery & Security", icon: ShieldAlert, testId: "partner-tab-security" },
          ].map(({ key, label, icon: Icon, testId }) => {
            const active = activeTab === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(key)}
                data-testid={testId}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.65rem 1rem",
                  border: "none",
                  background: "transparent",
                  borderBottom: active ? "2px solid #1E3A8A" : "2px solid transparent",
                  color: active ? "#1E3A8A" : "#64748B",
                  fontWeight: active ? 700 : 500,
                  fontSize: "0.82rem",
                  cursor: "pointer",
                  transition: "color 0.15s ease, border-color 0.15s ease",
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        <div className="hw-exec__drawer-body">
          {activeTab === "security" ? (
            <div data-testid="partner-tab-security-panel">
              <AdminPartnerRecovery partnerId={partner.partner_id} embedded />
            </div>
          ) : activeTab === "lifecycle" ? (
            <div data-testid="partner-tab-lifecycle-panel">
              <PartnerLifecyclePanel partnerId={partner.partner_id} />
            </div>
          ) : (
          <>
          {freshToken && (freshToken.token || freshToken.subscription?.secret) && (
            <div className="hw-partners__section" style={{ background: "#0F172A", color: "#F8FAFC" }}
                 data-testid="fresh-secret">
              <h3 style={{ margin: "0 0 0.4rem", color: "#F8FAFC" }}>
                Copy this secret now — it will not be shown again
              </h3>
              {freshToken.token && (
                <>
                  <div style={{ color: "#93C5FD", fontSize: "0.75rem" }}>Client ID: <code>{freshToken.id}</code></div>
                  <div className="hw-partners__token-reveal" data-testid="fresh-token">{freshToken.token}</div>
                </>
              )}
              {freshToken.subscription?.secret && (
                <>
                  <div style={{ color: "#93C5FD", fontSize: "0.75rem" }}>
                    Subscription secret (subscription_id: <code>{freshToken.subscription.subscription_id}</code>):
                  </div>
                  <div className="hw-partners__token-reveal">{freshToken.subscription.secret}</div>
                </>
              )}
              <button className="hw-partners__btn" onClick={onClearFresh}>Got it</button>
            </div>
          )}

          {/* No Portal Owner banner */}
          {showNoOwnerBanner && (
            <div
              className="hw-partners__section"
              data-testid="partner-no-owner-banner"
              style={{
                background: "#FFFBEB", border: "1px solid #FCD34D",
                display: "flex", gap: "0.75rem", alignItems: "flex-start",
              }}
            >
              <AlertTriangle className="w-5 h-5" style={{ color: "#B45309", flexShrink: 0, marginTop: "0.15rem" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: "#92400E" }}>
                  Partner Organization created successfully.
                </div>
                <div style={{ fontSize: "0.8rem", color: "#92400E", marginTop: "0.2rem" }}>
                  This organization does not yet have a Portal Owner.
                  Invite a Portal Owner to activate the Partner Portal.
                </div>
              </div>
              <button
                className="hw-partners__btn hw-partners__btn--primary"
                onClick={onOpenInviteOwner}
                data-testid="partner-invite-owner-banner-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Invite Portal Owner
              </button>
            </div>
          )}

          {showNewKey && (
            <NewKeyForm partner={partner} onCancel={onCancelNewKey}
                         onSubmit={onSubmitNewKey} catalog={catalog} />
          )}
          {showNewSub && (
            <NewSubForm partner={partner} events={events}
                         onCancel={onCancelNewSub} onSubmit={onSubmitNewSub} />
          )}
          {showInviteOwner && (
            <InvitePortalOwnerForm
              partner={partner}
              onCancel={onCancelInviteOwner}
              onSubmit={onSubmitInviteOwner}
            />
          )}

          {/* Portal Owner section — visible for portal-enabled org types */}
          {supportsPortal && (
            <PortalOwnerSection
              partner={partner}
              portalOwnerStatus={portalOwnerStatus}
              onOpenInviteOwner={onOpenInviteOwner}
              onInvitationAction={onInvitationAction}
              freshActivationUrls={freshActivationUrls}
            />
          )}

          <div className="hw-partners__section">
            <h3 style={{ margin: "0 0 0.5rem" }}>Allowed Scopes</h3>
            <div>{(partner.allowed_scopes || []).map((s) => <span key={s} className="hw-dev__chip">{s}</span>)}</div>
          </div>

          <div className="hw-partners__section" data-testid="partner-keys-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}><KeyRound className="w-4 h-4" style={{ display: "inline", verticalAlign: "text-bottom" }} /> API Keys</h3>
              <button className="hw-partners__btn hw-partners__btn--primary" onClick={onMintKey}
                      data-testid="partner-mint-key">
                <Plus className="w-3 h-3" /> Mint key
              </button>
            </div>
            {usage && (
              <div style={{ margin: "0.5rem 0", fontSize: "0.78rem", color: "#475569" }}>
                {usage.active_keys} active · {usage.revoked_keys} revoked ·
                {" "}{usage.total_requests} total requests · last used {usage.last_used_at || "—"}
              </div>
            )}
            {keys.length === 0 ? (
              <div style={{ color: "#64748B", fontSize: "0.85rem", padding: "0.5rem 0" }}>No keys yet.</div>
            ) : (
              <table className="hw-partners__table" style={{ marginTop: "0.5rem" }}>
                <thead>
                  <tr><th>Name</th><th>Prefix</th><th>Scopes</th><th>Requests</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id}>
                      <td>{k.name}</td>
                      <td><code>gv_{k.prefix}…</code></td>
                      <td>{(k.scopes || []).length}</td>
                      <td>{k.request_count || 0}</td>
                      <td>{k.revoked ? "revoked" : "active"}</td>
                      <td>
                        {!k.revoked && (
                          <button className="hw-partners__btn hw-partners__btn--danger"
                                  onClick={() => onRevokeKey(k.id)}
                                  data-testid={`revoke-key-${k.id}`}>
                            <Trash2 className="w-3 h-3" /> Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="hw-partners__section" data-testid="partner-subs-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}><Webhook className="w-4 h-4" style={{ display: "inline", verticalAlign: "text-bottom" }} /> Webhook Subscriptions</h3>
              <button className="hw-partners__btn hw-partners__btn--primary" onClick={onNewSub}
                      data-testid="partner-new-sub">
                <Plus className="w-3 h-3" /> New subscription
              </button>
            </div>
            {subs.length === 0 ? (
              <div style={{ color: "#64748B", fontSize: "0.85rem", padding: "0.5rem 0" }}>No subscriptions yet.</div>
            ) : (
              <table className="hw-partners__table" style={{ marginTop: "0.5rem" }}>
                <thead>
                  <tr><th>Target</th><th>Events</th><th>Delivered</th><th>Fails</th><th>Active</th><th></th></tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.subscription_id}>
                      <td style={{ maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis" }}
                          title={s.target_url}>{s.target_url}</td>
                      <td>{(s.event_types || []).length}</td>
                      <td>{s.delivery_count || 0}</td>
                      <td>{s.failure_count || 0}</td>
                      <td>{s.active ? "yes" : "no"}</td>
                      <td style={{ display: "flex", gap: "0.3rem" }}>
                        <button className="hw-partners__btn" onClick={() => onRotateSecret(s.subscription_id)}
                                data-testid={`rotate-sub-${s.subscription_id}`}>Rotate</button>
                        <button className="hw-partners__btn hw-partners__btn--danger"
                                onClick={() => onDeleteSub(s.subscription_id)}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Portal Owner section — invitation lifecycle + activation status.       */
/* --------------------------------------------------------------------- */

function PortalOwnerSection({ partner, portalOwnerStatus, onOpenInviteOwner,
                                onInvitationAction, freshActivationUrls = {} }) {
  const st = portalOwnerStatus || {};
  const latest = st.latest_invitation || null;
  const owner = st.portal_owner || null;
  const invitations = st.invitations || [];
  return (
    <div className="hw-partners__section" data-testid="portal-owner-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>
          <UserPlus className="w-4 h-4" style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.35rem" }} />
          Portal Owner
        </h3>
        {(!latest || latest.status === "revoked" || latest.status === "expired") && (
          <button
            className="hw-partners__btn hw-partners__btn--primary"
            onClick={onOpenInviteOwner}
            data-testid="portal-owner-invite-btn"
          >
            <UserPlus className="w-3 h-3" /> Invite Portal Owner
          </button>
        )}
      </div>

      {/* Snapshot grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.6rem",
          marginTop: "0.6rem",
        }}
        data-testid="portal-owner-snapshot"
      >
        <SnapshotTile
          icon={<Mail className="w-3.5 h-3.5" />}
          label="Portal Owner"
          value={
            owner?.email
              ? (<span>{owner?.name || owner.email}<br/><span style={{ fontSize: "0.7rem", color: "#64748B" }}>{owner.email}</span></span>)
              : (latest?.portal_owner_email
                  ? (<span>{latest.portal_owner_name || latest.portal_owner_email}<br/><span style={{ fontSize: "0.7rem", color: "#64748B" }}>{latest.portal_owner_email}</span></span>)
                  : "Not invited yet")
          }
          testid="po-tile-owner"
        />
        <SnapshotTile
          icon={<Clock className="w-3.5 h-3.5" />}
          label="Invitation Status"
          value={latest ? <InvBadge status={latest.status} /> : "—"}
          testid="po-tile-status"
        />
        <SnapshotTile
          icon={<Send className="w-3.5 h-3.5" />}
          label="Last Invitation Sent"
          value={fmtDate(latest?.sent_at || latest?.last_resent_at)}
          testid="po-tile-sent"
        />
        <SnapshotTile
          icon={<CalendarClock className="w-3.5 h-3.5" />}
          label="Invitation Expires"
          value={fmtDate(latest?.token_expires_at)}
          testid="po-tile-expires"
        />
        <SnapshotTile
          icon={<CheckCircle2 className="w-3.5 h-3.5" />}
          label="Portal Activated"
          value={owner?.accepted_at ? fmtDate(owner.accepted_at) : "—"}
          testid="po-tile-activated"
        />
        <SnapshotTile
          icon={<Clock className="w-3.5 h-3.5" />}
          label="Last Login"
          value={fmtDate(owner?.last_login_at)}
          testid="po-tile-last-login"
        />
      </div>

      {/* Latest invitation actions + copy link */}
      {latest && (
        <div
          className="hw-partners__section"
          style={{ marginTop: "0.75rem", padding: "0.75rem 0.9rem", background: "#F8FAFC" }}
          data-testid={`po-invitation-${latest.id.slice(0, 8)}`}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: "0.75rem", color: "#64748B" }}>
                Current invitation · resent {latest.resent_count || 0} time(s)
              </div>
              <div style={{ fontWeight: 600, marginTop: "0.15rem" }}>
                {latest.portal_owner_name || latest.portal_owner_email}
                {" "}<InvBadge status={latest.status} />
              </div>
            </div>
          </div>

          {freshActivationUrls[latest.id] && (
            <CopyLinkPanel url={freshActivationUrls[latest.id]} invId={latest.id} />
          )}

          {latest.status !== "activated" && latest.status !== "revoked" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.6rem" }}>
              <button
                className="hw-partners__btn hw-partners__btn--primary"
                onClick={() => onInvitationAction(latest.id, "resend", { reason: "admin_partners_resend" }, "Resend")}
                data-testid={`po-resend-${latest.id.slice(0, 8)}`}
              >
                <Send className="w-3 h-3" /> Resend
              </button>
              <button
                className="hw-partners__btn"
                onClick={() => onInvitationAction(latest.id, "regenerate", {}, "Regenerate")}
                data-testid={`po-regen-${latest.id.slice(0, 8)}`}
              >
                <RotateCcw className="w-3 h-3" /> Regenerate
              </button>
              <button
                className="hw-partners__btn"
                onClick={() => onInvitationAction(latest.id, "extend", { additional_days: 7 }, "Extend +7 days")}
                data-testid={`po-extend-${latest.id.slice(0, 8)}`}
              >
                <CalendarClock className="w-3 h-3" /> +7 days
              </button>
              <button
                className="hw-partners__btn hw-partners__btn--danger"
                onClick={() => {
                  const reason = window.prompt("Reason for revoking this invitation?");
                  if (!reason || reason.trim().length < 2) return;
                  onInvitationAction(latest.id, "revoke", { reason: reason.trim() }, "Revoke");
                }}
                data-testid={`po-revoke-${latest.id.slice(0, 8)}`}
              >
                <Ban className="w-3 h-3" /> Revoke
              </button>
            </div>
          )}
        </div>
      )}

      {/* Historical invitations list (>1) */}
      {invitations.length > 1 && (
        <details style={{ marginTop: "0.6rem" }} data-testid="po-history">
          <summary style={{ cursor: "pointer", fontSize: "0.78rem", color: "#64748B" }}>
            {invitations.length - 1} earlier invitation(s)
          </summary>
          <table className="hw-partners__table" style={{ marginTop: "0.4rem" }}>
            <thead>
              <tr>
                <th>Email</th><th>Status</th><th>Created</th><th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {invitations.slice(1).map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.portal_owner_email}</td>
                  <td><InvBadge status={inv.status} /></td>
                  <td>{fmtDate(inv.created_at)}</td>
                  <td>{fmtDate(inv.token_expires_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}


function SnapshotTile({ icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        border: "1px solid #E5E5E0", borderRadius: "0.6rem",
        padding: "0.55rem 0.7rem", background: "#FFF",
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: "0.3rem",
        fontSize: "0.68rem", textTransform: "uppercase",
        letterSpacing: "0.05em", color: "#94A3B8", marginBottom: "0.25rem",
      }}>
        {icon} <span>{label}</span>
      </div>
      <div style={{ fontSize: "0.85rem", color: "#0F172A", fontWeight: 500 }}>
        {value ?? "—"}
      </div>
    </div>
  );
}


function InvBadge({ status }) {
  const tone = INV_TONE[status] || "info";
  const bg = { warning: "#FEF3C7", info: "#DBEAFE", success: "#DCFCE7", danger: "#FEE2E2" }[tone];
  const fg = { warning: "#92400E", info: "#1E40AF", success: "#166534", danger: "#991B1B" }[tone];
  return (
    <span style={{
      display: "inline-block", fontSize: "0.65rem", padding: "0.1rem 0.5rem",
      borderRadius: "999px", background: bg, color: fg, fontWeight: 600,
      textTransform: "uppercase", letterSpacing: "0.03em",
    }}>
      {status}
    </span>
  );
}


function CopyLinkPanel({ url, invId }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Activation link copied");
    } catch {
      toast.error("Copy failed — select the URL and copy manually.");
    }
  };
  return (
    <div
      data-testid={`po-fresh-link-${invId.slice(0, 8)}`}
      style={{
        marginTop: "0.6rem", border: "1px solid #FCD34D",
        background: "#FFFBEB", borderRadius: "0.6rem", padding: "0.55rem 0.7rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.3rem",
                     color: "#92400E", fontSize: "0.7rem",
                     textTransform: "uppercase", letterSpacing: "0.05em" }}>
        <LinkIcon className="w-3 h-3" /> One-time activation link
      </div>
      <div style={{ fontSize: "0.72rem", color: "#92400E", margin: "0.25rem 0" }}>
        Copy this link now — it will not be shown again after you leave this page.
        Share out-of-band if the email did not arrive.
      </div>
      <div style={{ display: "flex", gap: "0.35rem" }}>
        <input
          readOnly value={url} onFocus={(e) => e.target.select()}
          data-testid={`po-fresh-link-input-${invId.slice(0, 8)}`}
          style={{
            flex: 1, minWidth: 0, fontFamily: "monospace",
            fontSize: "0.7rem", padding: "0.35rem 0.5rem",
            border: "1px solid #FCD34D", borderRadius: "0.4rem",
            background: "#FFF", color: "#7C2D12",
          }}
        />
        <button
          className="hw-partners__btn"
          style={{ background: "#F59E0B", color: "#FFF" }}
          onClick={copy}
          data-testid={`po-copy-link-${invId.slice(0, 8)}`}
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
    </div>
  );
}


/* --------------------------------------------------------------------- */
/* Invite Portal Owner form                                              */
/* --------------------------------------------------------------------- */

function InvitePortalOwnerForm({ partner, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    portal_owner_email: partner.contact_email || "",
    portal_owner_name: partner.contact_name || "",
    job_title: "",
    ttl_days: 7,
  });
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.portal_owner_email);
  return (
    <section
      className="hw-partners__section"
      data-testid="invite-portal-owner-form"
      style={{ borderLeft: "3px solid #1E3A8A" }}
    >
      <h3 style={{ margin: 0 }}>
        <UserPlus className="w-4 h-4" style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.35rem" }} />
        Invite Portal Owner for {partner.name}
      </h3>
      <p style={{ fontSize: "0.75rem", color: "#64748B", margin: "0.4rem 0 0.6rem" }}>
        The Portal Owner will receive a branded activation email and can then log
        into the Partner Portal. If email delivery fails, a one-time activation
        link will appear here so you can share it out-of-band.
      </p>
      <div className="hw-partners__form-grid">
        <label className="hw-partners__label">Portal Owner Name
          <input
            value={form.portal_owner_name}
            onChange={(e) => setForm({ ...form, portal_owner_name: e.target.value })}
            data-testid="ipo-name"
            placeholder="Full name"
          />
        </label>
        <label className="hw-partners__label">Portal Owner Email *
          <input
            type="email"
            value={form.portal_owner_email}
            onChange={(e) => setForm({ ...form, portal_owner_email: e.target.value })}
            data-testid="ipo-email"
            placeholder="owner@example.org"
          />
        </label>
        <label className="hw-partners__label">Job Title
          <input
            value={form.job_title}
            onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            data-testid="ipo-job-title"
            placeholder="Optional"
          />
        </label>
        <label className="hw-partners__label">Link Expires (days)
          <input
            type="number"
            min={1}
            max={90}
            value={form.ttl_days}
            onChange={(e) => setForm({ ...form, ttl_days: Number(e.target.value) || 7 })}
            data-testid="ipo-ttl"
          />
        </label>
      </div>
      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.4rem" }}>
        <button className="hw-partners__btn" onClick={onCancel} data-testid="ipo-cancel">Cancel</button>
        <button
          className="hw-partners__btn hw-partners__btn--primary"
          disabled={!emailValid}
          onClick={() => onSubmit({
            portal_owner_email: form.portal_owner_email.trim(),
            portal_owner_name: form.portal_owner_name.trim() || undefined,
            job_title: form.job_title.trim() || undefined,
            ttl_days: form.ttl_days,
          })}
          data-testid="ipo-submit"
        >
          <Send className="w-3 h-3" /> Send Invitation
        </button>
      </div>
    </section>
  );
}



// ─────────────────────────────────────────────────────────────────────
// Iter FE · Partner Lifecycle Panel
// Admin-facing tab: shows current state, valid next-states, transition
// history. All transitions are EXPLICIT and require an audit reason.
// ─────────────────────────────────────────────────────────────────────
const LIFECYCLE_TONE = {
  intake: { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF" },
  onboarding: { bg: "#FEF3C7", border: "#FDE68A", text: "#92400E" },
  live: { bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46" },
  paused: { bg: "#FFFBEB", border: "#FCD34D", text: "#78350F" },
  offboarded: { bg: "#FEE2E2", border: "#FCA5A5", text: "#991B1B" },
  terminal: { bg: "#F1F5F9", border: "#CBD5E1", text: "#334155" },
};

function PartnerLifecyclePanel({ partnerId }) {
  const [lifecycle, setLifecycle] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cur }, { data: hist }] = await Promise.all([
        api.get(`/admin/partners/${partnerId}/lifecycle`),
        api.get(`/admin/partners/${partnerId}/lifecycle/transitions`),
      ]);
      setLifecycle(cur);
      setHistory(hist.transitions || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load lifecycle");
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submit = async () => {
    if (!pendingTransition) return;
    const minLen = pendingTransition.min_reason_length;
    if (reason.trim().length < minLen) {
      toast.error(`Reason must be at least ${minLen} characters.`);
      return;
    }
    setBusy(true);
    try {
      const body = { to_state: pendingTransition.key, reason: reason.trim() };
      if (notes.trim()) body.notes = notes.trim();
      await api.post(`/admin/partners/${partnerId}/lifecycle/transition`, body);
      toast.success(`Partner moved to ${pendingTransition.label}.`);
      setPendingTransition(null);
      setReason("");
      setNotes("");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e) || "Transition failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="partner-lifecycle-loading" style={{ padding: "1rem", color: "#64748B" }}>
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading lifecycle…
      </div>
    );
  }
  if (!lifecycle) return null;

  const tone = LIFECYCLE_TONE[lifecycle.current_state.phase] || LIFECYCLE_TONE.terminal;

  return (
    <div className="hw-partners__section" data-testid="partner-lifecycle-panel"
          style={{ padding: "1.15rem 1.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                     gap: "0.6rem", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: "0.68rem", textTransform: "uppercase",
                          letterSpacing: "0.08em", color: "#64748B" }}>
            Current lifecycle state
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem",
                          padding: "0.35rem 0.75rem", marginTop: "0.3rem",
                          background: tone.bg, border: `1px solid ${tone.border}`,
                          color: tone.text, borderRadius: 999,
                          fontWeight: 600, fontSize: "0.88rem" }}
                data-testid="partner-lifecycle-current-state">
            <Activity className="w-3.5 h-3.5" /> {lifecycle.current_state.label}
          </div>
          <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: "0.35rem",
                          maxWidth: 520 }}>
            {lifecycle.current_state.description}
          </div>
        </div>
        <div style={{ fontSize: "0.72rem", color: "#64748B", textAlign: "right" }}>
          <div>History count: <strong data-testid="partner-lifecycle-history-count">{lifecycle.history_count}</strong></div>
          <div>Version: <code>{lifecycle.lifecycle_version}</code></div>
        </div>
      </div>

      <div style={{ marginTop: "1rem" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155",
                        marginBottom: "0.45rem" }}>
          Available transitions
        </div>
        {lifecycle.next_states.length === 0 ? (
          <div data-testid="partner-lifecycle-terminal-note"
                style={{ fontSize: "0.78rem", color: "#64748B",
                          padding: "0.55rem 0.75rem", background: "#F1F5F9",
                          borderRadius: 6, border: "1px dashed #CBD5E1" }}>
            Terminal state — no further transitions available.
          </div>
        ) : (
          <>
            {lifecycle.next_states.every((n) => n.is_destructive) && (
              <div data-testid="partner-lifecycle-all-destructive-note"
                    style={{ fontSize: "0.72rem", color: "#78350F",
                              padding: "0.4rem 0.6rem", background: "#FFFBEB",
                              border: "1px solid #FDE68A", borderRadius: 6,
                              marginBottom: "0.5rem" }}>
                Every available transition from this state changes the partner&apos;s
                operational posture and requires an audit reason.
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            {lifecycle.next_states.map((ns) => (
              <button
                key={ns.key}
                onClick={() => { setPendingTransition(ns); setReason(""); setNotes(""); }}
                data-testid={`partner-lifecycle-next-${ns.key}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.35rem",
                  padding: "0.35rem 0.7rem", borderRadius: 6,
                  border: ns.is_destructive ? "1px solid #FCA5A5" : "1px solid #C7D2FE",
                  background: ns.is_destructive ? "#FEF2F2" : "#EFF6FF",
                  color: ns.is_destructive ? "#991B1B" : "#1E3A8A",
                  fontSize: "0.78rem", fontWeight: 500, cursor: "pointer",
                }}>
                {ns.is_destructive && <AlertTriangle className="w-3 h-3" />}
                <ArrowRight className="w-3 h-3" /> {ns.label}
              </button>
            ))}
            </div>
          </>
        )}
      </div>

      {pendingTransition && (
        <div data-testid="partner-lifecycle-transition-modal"
              style={{ marginTop: "1rem", padding: "0.85rem 1rem",
                        background: "#F8FAFC", border: "1px solid #E2E8F0",
                        borderRadius: 8 }}>
          <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0F172A",
                          marginBottom: "0.35rem" }}>
            Transition to <span style={{ color: pendingTransition.is_destructive ? "#991B1B" : "#1E3A8A" }}>
              {pendingTransition.label}
            </span>
          </div>
          <div style={{ fontSize: "0.72rem", color: "#64748B", marginBottom: "0.6rem" }}>
            {pendingTransition.is_destructive
              ? "Destructive transition — requires ≥30 character audit reason."
              : "Requires ≥20 character audit reason."}
          </div>
          <label style={{ display: "block", fontSize: "0.72rem", color: "#475569",
                            marginBottom: "0.35rem" }}>
            Audit reason (required)
            <textarea
              rows={3} maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="partner-lifecycle-reason"
              placeholder={pendingTransition.is_destructive
                ? "e.g. Contract violation review — pausing until legal completes assessment."
                : "e.g. Application reviewed and cleared."}
              style={{ width: "100%", marginTop: "0.25rem", padding: "0.4rem 0.55rem",
                        border: "1px solid #CBD5E1", borderRadius: 6, fontSize: "0.82rem",
                        fontFamily: "inherit", background: "#FFFFFF" }}
            />
            <div data-testid="partner-lifecycle-reason-count"
                  style={{ fontSize: "0.68rem", color: "#94A3B8", marginTop: "0.2rem" }}>
              {reason.length}/{pendingTransition.min_reason_length} characters minimum
            </div>
          </label>
          <label style={{ display: "block", fontSize: "0.72rem", color: "#475569",
                            marginTop: "0.55rem" }}>
            Notes (optional)
            <textarea
              rows={2} maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="partner-lifecycle-notes"
              placeholder="Optional freeform context…"
              style={{ width: "100%", marginTop: "0.25rem", padding: "0.4rem 0.55rem",
                        border: "1px solid #CBD5E1", borderRadius: 6, fontSize: "0.82rem",
                        fontFamily: "inherit", background: "#FFFFFF" }}
            />
          </label>
          <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.65rem",
                          justifyContent: "flex-end" }}>
            <button
              onClick={() => { setPendingTransition(null); setReason(""); setNotes(""); }}
              disabled={busy}
              data-testid="partner-lifecycle-cancel"
              className="hw-partners__btn"
              style={{ background: "#FFFFFF", border: "1px solid #CBD5E1" }}>
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy || reason.trim().length < pendingTransition.min_reason_length}
              data-testid="partner-lifecycle-confirm"
              className="hw-partners__btn"
              style={{
                background: pendingTransition.is_destructive ? "#B91C1C" : "#1E3A8A",
                color: "#FFFFFF",
                opacity: (busy || reason.trim().length < pendingTransition.min_reason_length) ? 0.6 : 1,
              }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null}
              Confirm transition
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: "1.1rem" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155",
                        marginBottom: "0.4rem" }}>
          Transition history
        </div>
        {history.length === 0 ? (
          <div data-testid="partner-lifecycle-history-empty"
                style={{ fontSize: "0.78rem", color: "#64748B",
                          padding: "0.55rem 0.75rem", background: "#F8FAFC",
                          borderRadius: 6 }}>
            No transitions recorded yet. All partner records default to
            <code style={{ margin: "0 0.25rem" }}>active</code> until the first
            explicit transition.
          </div>
        ) : (
          <div data-testid="partner-lifecycle-history-table"
                style={{ border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
            {history.map((h) => (
              <div key={h.id}
                    style={{ display: "grid",
                              gridTemplateColumns: "1fr 1fr 2fr",
                              gap: "0.5rem",
                              padding: "0.55rem 0.8rem",
                              borderBottom: "1px solid #F1F5F9",
                              alignItems: "center",
                              fontSize: "0.78rem" }}>
                <div style={{ color: "#64748B" }}>
                  <div style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.7rem" }}>
                    {new Date(h.transitioned_at).toLocaleString()}
                  </div>
                  <div style={{ fontSize: "0.68rem", color: "#94A3B8" }}>
                    {h.actor_email}
                  </div>
                </div>
                <div>
                  <span style={{ color: "#64748B" }}>{h.from_state_label || h.from_state}</span>
                  <ArrowRight className="w-3 h-3 inline mx-1" />
                  <strong style={{ color: "#0F172A" }}>{h.to_state_label}</strong>
                </div>
                <div style={{ color: "#334155" }}>
                  {h.reason}
                  {h.notes && (
                    <div style={{ fontSize: "0.7rem", color: "#94A3B8",
                                    marginTop: "0.2rem", fontStyle: "italic" }}>
                      {h.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
